import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ClientThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ForceChangePassword from "@/pages/ForceChangePassword";
import AcceptContract from "@/pages/AcceptContract";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Catalog from "@/pages/Catalog";
import ScooterDetail from "@/pages/ScooterDetail";
import Checkout from "@/pages/Checkout";
import PaymentResult from "@/pages/PaymentResult";
import MyRentals from "@/pages/MyRentals";
import Profile from "@/pages/Profile";
import Home from "@/pages/Home";
import CustomerLayout from "@/layouts/CustomerLayout";
import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminFleet from "@/pages/admin/Fleet";
import AdminFleetCalendar from "@/pages/admin/FleetCalendar";
import AdminBookings from "@/pages/admin/Bookings";
import AdminPromoCodes from "@/pages/admin/PromoCodes";
import AdminHotelConventions from "@/pages/admin/HotelConventions";
import AdminContracts from "@/pages/admin/AdminContracts";
import AdminContractNew from "@/pages/admin/AdminContractNew";
import AdminContractTemplate from "@/pages/admin/AdminContractTemplate";
import AdminPrivacyTemplate from "@/pages/admin/AdminPrivacyTemplate";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminRecover from "@/pages/AdminRecover";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDossier from "@/pages/admin/AdminCustomerDossier";
import BusinessClosures from "@/pages/admin/BusinessClosures";
import VerbalePage from "@/pages/admin/VerbalePage";
import CookieBanner from "@/components/CookieBanner";

function ProtectedRoute({ children, role, requireContract = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="text-zinc-400">Caricamento…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/home"} replace />;
  }
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (
    requireContract &&
    user.role === "cliente" &&
    !user.contract_accepted &&
    location.pathname !== "/accept-contract"
  ) {
    return <Navigate to="/accept-contract" replace />;
  }
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user && user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user && user.role === "cliente" && !user.contract_accepted) {
    return <Navigate to="/accept-contract" replace />;
  }
  // Guests and authenticated customers both land on /home (public since Iter 49).
  return <Navigate to="/home" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ClientThemeProvider>
        <BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
          <CookieBanner />
          <Routes>
            <Route path="/" element={<RoleRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin/recover" element={<AdminRecover />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={
              <ProtectedRoute requireContract={false}>
                <ForceChangePassword />
              </ProtectedRoute>
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />

            <Route path="/accept-contract" element={
              <ProtectedRoute role="cliente" requireContract={false}>
                <AcceptContract />
              </ProtectedRoute>
            } />

            <Route element={<CustomerLayout />}>
              <Route path="/home" element={<Home />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/scooter/:id" element={<ScooterDetail />} />
            </Route>

            <Route element={<ProtectedRoute role="cliente"><CustomerLayout /></ProtectedRoute>}>
              <Route path="/checkout/:bookingId" element={<Checkout />} />
              <Route path="/checkout/success" element={<PaymentResult />} />
              <Route path="/checkout/cancel" element={<PaymentResult />} />
              <Route path="/my-rentals" element={<MyRentals />} />
              <Route path="/profile" element={<Profile />} />
            </Route>

            <Route element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/fleet" element={<AdminFleet />} />
              <Route path="/admin/calendar" element={<AdminFleetCalendar />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/contracts" element={<AdminContracts />} />
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route path="/admin/customers/:customerId" element={<AdminCustomerDossier />} />
              <Route path="/admin/contracts/new" element={<AdminContractNew />} />
              <Route path="/admin/contracts/:contractId/delivery" element={<VerbalePage mode="delivery" />} />
              <Route path="/admin/contracts/:contractId/return" element={<VerbalePage mode="return" />} />
              <Route path="/admin/contract-template" element={<AdminContractTemplate />} />
              <Route path="/admin/privacy-template" element={<AdminPrivacyTemplate />} />
              <Route path="/admin/profile" element={<AdminProfile />} />
              <Route path="/admin/team" element={<AdminTeam />} />
              <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
              <Route path="/admin/hotel-conventions" element={<AdminHotelConventions />} />
              <Route path="/admin/business-closures" element={<BusinessClosures />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ClientThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
